import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import { useNavigate } from 'react-router-dom';

const MEDALLAS = ['🥇', '🥈', '🥉'];

const RankingGeneral = ({ session }) => {
  const navigate = useNavigate();
  const [tipo, setTipo]             = useState('tactico');
  const [ranking, setRanking]       = useState([]);
  const [rankingClanes, setRankingClanes] = useState([]);
  const [tab, setTab]               = useState('individual');
  const [loading, setLoading]       = useState(true);
  const [loadingClanes, setLoadingClanes] = useState(false);

  // Fecha activa
  const [vistaFecha, setVistaFecha]       = useState('general'); // 'general' | 'fecha_actual'
  const [rankingFechaActual, setRankingFechaActual] = useState([]);
  const [jornadaActiva, setJornadaActiva]           = useState(null);
  const [loadingFecha, setLoadingFecha]             = useState(false);

  const [rankingLibre, setRankingLibre] = useState([]);
  const [loadingLibre, setLoadingLibre] = useState(true);
  const [rankingFechaLibre, setRankingFechaLibre] = useState([]);
  const [loadingFechaLibre, setLoadingFechaLibre] = useState(false);
  const [vistaFechaTactico, setVistaFechaTactico] = useState('general');
  const [vistaFechaLibre, setVistaFechaLibre] = useState('general');
  const [tabMobile, setTabMobile] = useState('tactico');
  const [rankingPenales, setRankingPenales] = useState([]);
  const [loadingPenales, setLoadingPenales] = useState(true);

  useEffect(() => { cargarRanking(); }, [tipo]);
  useEffect(() => { cargarRankingClanes(); }, []); // eslint-disable-line
  useEffect(() => {
    const cargarPenales = async () => {
      setLoadingPenales(true);
      const { data } = await supabase
        .from('perfiles')
        .select('id, nickname, avatar_color, clan_tag, racha_penales')
        .gt('racha_penales', 0)
        .order('racha_penales', { ascending: false })
        .limit(10);
      setRankingPenales(data || []);
      setLoadingPenales(false);
    };
    cargarPenales();
  }, []);

  // Función genérica para cargar ranking por fecha — acepta tipo como parámetro
  const cargarRankingFecha = async (tipoParam, setData, setLoading) => {
    setLoading(true);
    try {
      const { data: ventana } = await supabase
        .from('ventana_transferencias')
        .select('numero_fecha')
        .eq('activa', true)
        .order('numero_fecha', { ascending: false })
        .limit(1)
        .maybeSingle();

      const jornada = ventana?.numero_fecha;
      if (!jornada) { setData([]); setLoading(false); return; }
      setJornadaActiva(jornada);

      const { data: torneos } = await supabase.from('torneos').select('id').eq('tipo', tipoParam);
      const torneoIds = (torneos || []).map(t => t.id);
      if (!torneoIds.length) { setData([]); setLoading(false); return; }

      // Buscar última jornada con puntos reales
      let fechaIds = [];
      let jornadaMostrada = jornada;
      for (let j = jornada; j >= 1; j--) {
        const { data: fechas } = await supabase.from('torneo_fechas').select('id').in('torneo_id', torneoIds).eq('numero_fecha', j);
        const ids = (fechas || []).map(f => f.id);
        if (!ids.length) continue;
        const { count } = await supabase.from('torneo_puntos').select('id', { count: 'exact', head: true }).in('fecha_id', ids).gt('puntos', 0);
        if (count > 0) { fechaIds = ids; jornadaMostrada = j; break; }
      }
      setJornadaActiva(jornadaMostrada);
      if (!fechaIds.length) { setData([]); setLoading(false); return; }

      const { data: puntos } = await supabase.from('torneo_puntos').select('user_id, puntos').in('fecha_id', fechaIds);

      const acumulado = {};
      (puntos || []).forEach(p => {
        if (!acumulado[p.user_id]) {
          acumulado[p.user_id] = { puntos: p.puntos || 0 };
        } else if ((p.puntos || 0) > acumulado[p.user_id].puntos) {
          acumulado[p.user_id].puntos = p.puntos || 0;
        }
      });

      if (!Object.keys(acumulado).length) { setRankingFechaActual([]); setLoadingFecha(false); return; }

      // Perfiles
      const { data: perfiles } = await supabase
        .from('perfiles')
        .select('id, nickname, avatar_color, clan_tag')
        .in('id', Object.keys(acumulado));
      const mapaPerfiles = {};
      (perfiles || []).forEach(p => { mapaPerfiles[p.id] = p; });

      const sorted = Object.entries(acumulado)
        .map(([user_id, data]) => ({
          user_id,
          puntos: data.puntos,
          presupuesto_gastado: data.presupuesto_gastado,
          nickname:     mapaPerfiles[user_id]?.nickname     || user_id.slice(0, 6),
          clan_tag:     mapaPerfiles[user_id]?.clan_tag     || null,
          avatar_color: mapaPerfiles[user_id]?.avatar_color || '#00f2ff',
        }))
        .sort((a, b) => b.puntos !== a.puntos
          ? b.puntos - a.puntos
          : a.presupuesto_gastado - b.presupuesto_gastado
        );

      setData(sorted);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const cargarRankingFechaActual = () => cargarRankingFecha('tactico', setRankingFechaActual, setLoadingFecha);
  const cargarRankingFechaLibre  = () => cargarRankingFecha('libre', setRankingFechaLibre, setLoadingFechaLibre);

  const cargarRankingClanes = async () => {
    setLoadingClanes(true);
    const { data: clanes } = await supabase
      .from('clanes')
      .select('id, nombre, tag, emblema, color, trofeos, puntos_clan, medallas, guerras_ganadas');
    if (!clanes) { setLoadingClanes(false); return; }
    // Contar miembros por clan
    const { data: miembros } = await supabase
      .from('clan_miembros').select('clan_id');
    const miembrosMap = {};
    (miembros || []).forEach(m => { miembrosMap[m.clan_id] = (miembrosMap[m.clan_id] || 0) + 1; });
    // Armar ranking por cantidad de trofeos
    const ranked = clanes.map(c => ({
      ...c,
      totalTrofeos: (c.trofeos || []).length, puntosClan: c.puntos_clan || 0, medallasClan: c.medallas || 0,
      totalMiembros: miembrosMap[c.id] || 0,
    })).sort((a, b) => b.totalTrofeos - a.totalTrofeos || b.totalMiembros - a.totalMiembros);
    setRankingClanes(ranked);
    setLoadingClanes(false);
  };

  const cargarRanking = async () => {
    setLoading(true);
    // Traer todos los torneos del tipo
    const { data: torneosData } = await supabase.from('torneos').select('id').eq('tipo', tipo);
    const torneoIds = (torneosData || []).map(t => t.id);
    if (!torneoIds.length) { setRanking([]); setLoading(false); return; }

    // Traer todas las fechas de esos torneos
    const { data: fechasData } = await supabase.from('torneo_fechas').select('id, numero_fecha, torneo_id').in('torneo_id', torneoIds);
    const fechaIds = (fechasData || []).map(f => f.id);
    if (!fechaIds.length) { setRanking([]); setLoading(false); return; }

    // Traer puntos con numero_fecha para deduplicar por jornada (un usuario puede estar en varios torneos)
    const { data: puntosData } = await supabase.from('torneo_puntos')
      .select('user_id, puntos, fecha_id, fecha:torneo_fechas(numero_fecha)')
      .in('fecha_id', fechaIds);

    // Para cada (user, numero_fecha) tomar el MAX (evita duplicar puntos de múltiples torneos)
    const porUserJornada = {};
    (puntosData || []).forEach(p => {
      const jornada = p.fecha?.numero_fecha ?? p.fecha_id;
      const key = p.user_id + '_' + jornada;
      if (!porUserJornada[key] || p.puntos > porUserJornada[key]) porUserJornada[key] = p.puntos;
    });
    const acumulado = {};
    Object.entries(porUserJornada).forEach(([key, pts]) => {
      const uid = key.split('_')[0];
      acumulado[uid] = (acumulado[uid] || 0) + pts;
    });

    if (!Object.keys(acumulado).length) { setRanking([]); setLoading(false); return; }

    const userIds = Object.keys(acumulado);
    const { data: perfiles } = await supabase.from('perfiles').select('id, nickname, avatar_color, clan_tag').in('id', userIds);
    const mapaPerfiles = {};
    (perfiles || []).forEach(p => { mapaPerfiles[p.id] = p; });

    setRanking(Object.entries(acumulado).map(([uid, pts]) => ({
      user_id: uid, puntos: pts,
      nickname: mapaPerfiles[uid]?.nickname || uid.slice(0,6),
      clan_tag: mapaPerfiles[uid]?.clan_tag || null,
      avatar_color: mapaPerfiles[uid]?.avatar_color || '#00f2ff',
    })).sort((a, b) => b.puntos - a.puntos));
    setLoading(false);
  };

  // Cargar datos al montar — libre general + ambas fechas
  useEffect(() => {
    const cargarLibreGeneral = async () => {
      setLoadingLibre(true);
      const { data: torneos } = await supabase.from('torneos').select('id').eq('tipo', 'libre');
      const torneoIds = (torneos || []).map(t => t.id);
      if (!torneoIds.length) { setRankingLibre([]); setLoadingLibre(false); return; }
      const { data: fechas } = await supabase.from('torneo_fechas').select('id').in('torneo_id', torneoIds);
      const fechaIds = (fechas || []).map(f => f.id);
      if (!fechaIds.length) { setRankingLibre([]); setLoadingLibre(false); return; }
      const { data: puntosData } = await supabase.from('torneo_puntos')
        .select('user_id, puntos, fecha_id, fecha:torneo_fechas(numero_fecha)')
        .in('fecha_id', fechaIds);
      const porUserJornada = {};
      (puntosData || []).forEach(p => {
        const jornada = p.fecha?.numero_fecha ?? p.fecha_id;
        const key = p.user_id + '_' + jornada;
        if (!porUserJornada[key] || p.puntos > porUserJornada[key]) porUserJornada[key] = p.puntos;
      });
      const acum = {};
      Object.entries(porUserJornada).forEach(([key, pts]) => { const uid = key.split('_')[0]; acum[uid] = (acum[uid] || 0) + pts; });
      if (!Object.keys(acum).length) { setRankingLibre([]); setLoadingLibre(false); return; }
      const { data: perfiles } = await supabase.from('perfiles').select('id, nickname, avatar_color, clan_tag').in('id', Object.keys(acum));
      const map = {}; (perfiles || []).forEach(p => { map[p.id] = p; });
      setRankingLibre(Object.entries(acum).map(([uid, pts]) => ({ user_id: uid, puntos: pts, nickname: map[uid]?.nickname || uid.slice(0,6), clan_tag: map[uid]?.clan_tag || null, avatar_color: map[uid]?.avatar_color || '#a855f7' })).sort((a,b) => b.puntos - a.puntos));
      setLoadingLibre(false);
    };
    cargarLibreGeneral();
    cargarRankingFechaActual();
    cargarRankingFechaLibre();
  }, []); // eslint-disable-line
  const tipoColor = tipo === 'tactico' ? '#00f2ff' : '#a855f7';
  const miUserId  = session?.user?.id;

  const EMBLEMAS = { escudo_1:'🛡️', escudo_2:'⚔️', estrella:'⭐', corona:'👑', fuego:'🔥', rayo:'⚡', trofeo:'🏆', dragon:'🐉', aguila:'🦅', leon:'🦁', lobo:'🐺', toro:'🐂', planeta:'🌍', diamante:'💎', cohete:'🚀', calavera:'💀' };

  // Componente de lista de ranking reutilizable
  const ListaRanking = ({ datos, loading: isLoading, empty }) => {
    const MAX = 50;
    const STEP = 10;
    const [visible, setVisible] = React.useState(STEP);
    if (isLoading) return <p style={{ color: '#475569', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>Cargando...</p>;
    if (!datos.length) return <p style={{ color: '#334155', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>{empty || 'Sin datos'}</p>;
    const visibles = datos.slice(0, Math.min(visible, MAX));
    const hayMas = datos.length > visible && visible < MAX;
    const hayMenos = visible > STEP;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {visibles.map((r, i) => {
          const esMio = r.user_id === miUserId;
          const medal = i < 3 ? MEDALLAS[i] : null;
          const color = r.avatar_color || tipoColor;
          return (
            <div key={r.user_id}
              onClick={() => navigate(`/palmares/${r.user_id}`)}
              style={{ display: 'grid', gridTemplateColumns: '36px 1fr auto', padding: '9px 12px', background: esMio ? tipoColor + '10' : '#080d14', border: '1px solid ' + (esMio ? tipoColor + '44' : '#1e293b'), borderRadius: '8px', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: '0.2s' }}
              onMouseEnter={e => { if (!esMio) e.currentTarget.style.borderColor = tipoColor + '44'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = esMio ? tipoColor + '44' : '#1e293b'; }}
            >
              <div style={{ textAlign: 'center' }}>
                {medal ? <span style={{ fontSize: '1.1rem' }}>{medal}</span> : <span style={{ color: '#475569', fontWeight: 'bold', fontSize: '0.82rem' }}>#{i+1}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                  {r.nickname?.slice(0,2).toUpperCase()}
                </div>
                <span style={{ color: esMio ? tipoColor : '#e2e8f0', fontWeight: esMio ? 'bold' : '500', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.clan_tag ? <span style={{ color: '#64748b' }}>[{r.clan_tag}] </span> : ''}{r.nickname}
                  {esMio && <span style={{ fontSize: '0.65rem', color: tipoColor, marginLeft: '5px', border: '1px solid ' + tipoColor, padding: '1px 4px', borderRadius: '6px' }}>vos</span>}
                </span>
              </div>
              <span style={{ color: esMio ? tipoColor : '#94a3b8', fontWeight: 'bold', fontSize: '1rem', whiteSpace: 'nowrap' }}>
                {r.puntos} <span style={{ color: '#334155', fontSize: '0.7rem', fontWeight: 'normal' }}>pts</span>
              </span>
            </div>
          );
        })}
        {(hayMas || hayMenos) && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '6px' }}>
            {hayMenos && (
              <button onClick={() => setVisible(v => Math.max(STEP, v - STEP))}
                style={{ padding: '5px 14px', background: 'none', border: '1px solid #1e293b', borderRadius: '6px', color: '#475569', fontSize: '0.78rem', cursor: 'pointer' }}>
                ◀
              </button>
            )}
            <span style={{ color: '#334155', fontSize: '0.75rem', alignSelf: 'center' }}>
              {visibles.length} de {Math.min(datos.length, MAX)}
            </span>
            {hayMas && (
              <button onClick={() => setVisible(v => Math.min(MAX, v + STEP))}
                style={{ padding: '5px 14px', background: 'none', border: '1px solid #1e293b', borderRadius: '6px', color: '#475569', fontSize: '0.78rem', cursor: 'pointer' }}>
                ▶
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ranking-root" style={{ padding: '24px 20px', color: 'white', minHeight: 'calc(100vh - var(--navbar-h))', background: '#020b18' }}>
      {/* Título centrado */}
      <h1 style={{ textAlign: 'center', margin: '0 0 28px', fontSize: '1.6rem', letterSpacing: '2px', fontWeight: 'bold' }}>
        RANKING <span style={{ color: '#00f2ff' }}>GENERAL</span>
      </h1>

      {/* Tabs mobile — solo visibles en ≤768px */}
      <div className="ranking-tabs-mobile">
        {[
          { key: 'tactico', label: 'Táctico', color: '#00f2ff' },
          { key: 'libre',   label: 'Libre',   color: '#a855f7' },
          { key: 'clanes',  label: 'Clanes',  color: '#f59e0b' },
          { key: 'penales', label: 'Penales',  color: '#22c55e' },
        ].map(({ key, label, color }) => (
          <button key={key} onClick={() => setTabMobile(key)} style={{
            flex: 1, padding: '9px 4px', background: tabMobile === key ? color + '22' : 'transparent',
            border: `1px solid ${tabMobile === key ? color : '#1e293b'}`,
            borderRadius: '8px', color: tabMobile === key ? color : '#475569',
            fontWeight: 'bold', fontSize: '0.75rem', cursor: 'pointer', transition: '0.2s',
          }}>{label}</button>
        ))}
      </div>

      {/* 4 COLUMNAS */}
      <div className="ranking-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', maxWidth: '1400px', margin: '0 auto', alignItems: 'start' }}>

        {/* ── COLUMNA 1: TÁCTICO ── */}
        <div className={`ranking-col${tabMobile !== 'tactico' ? ' ranking-col-hidden' : ''}`} style={{ background: '#080d14', border: '1px solid #00f2ff33', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #0f172a', background: '#00f2ff08' }}>
            <h3 style={{ margin: 0, color: '#00f2ff', fontSize: '0.95rem', fontWeight: 'bold', letterSpacing: '1px' }}>TÁCTICO</h3>
          </div>
          <div style={{ padding: '12px' }}>
            {/* Sub-tabs general / fecha */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {[['general','General'],['fecha_actual','Fecha ' + (jornadaActiva||'1')]].map(([v,label]) => (
                <button key={v} onClick={() => { setTipo('tactico'); setVistaFechaTactico(v); if (v==='fecha_actual' && !rankingFechaActual.length) cargarRankingFechaActual(); }}
                  style={{ flex: 1, padding: '5px 8px', background: vistaFechaTactico===v ? '#00f2ff22' : '#0a0f19', border: '1px solid ' + (vistaFechaTactico===v ? '#00f2ff' : '#1e293b'), borderRadius: '6px', color: vistaFechaTactico===v ? '#00f2ff' : '#64748b', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s' }}>
                  {label}
                </button>
              ))}
            </div>
            {vistaFechaTactico === 'general' && <ListaRanking datos={tipo==='tactico' ? ranking : []} loading={tipo==='tactico' ? loading : false} empty="Sin datos tácticos" />}
            {vistaFechaTactico === 'fecha_actual' && <ListaRanking datos={tipo==='tactico' ? rankingFechaActual : []} loading={tipo==='tactico' ? loadingFecha : false} empty="Sin puntos para esta fecha" />}
          </div>
        </div>

        {/* ── COLUMNA 2: LIBRE ── */}
        <div className={`ranking-col${tabMobile !== 'libre' ? ' ranking-col-hidden' : ''}`} style={{ background: '#080d14', border: '1px solid #a855f733', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #0f172a', background: '#a855f708' }}>
            <h3 style={{ margin: 0, color: '#a855f7', fontSize: '0.95rem', fontWeight: 'bold', letterSpacing: '1px' }}>LIBRE</h3>
          </div>
          <div style={{ padding: '12px' }}>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {[['general','General'],['fecha_actual','Fecha ' + (jornadaActiva||'1')]].map(([v,label]) => (
                <button key={v} onClick={() => { setVistaFechaLibre(v); if (v==='fecha_actual' && !rankingFechaLibre.length) cargarRankingFechaLibre(); }}
                  style={{ flex: 1, padding: '5px 8px', background: vistaFechaLibre===v ? '#a855f722' : '#0a0f19', border: '1px solid ' + (vistaFechaLibre===v ? '#a855f7' : '#1e293b'), borderRadius: '6px', color: vistaFechaLibre===v ? '#a855f7' : '#64748b', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s' }}>
                  {label}
                </button>
              ))}
            </div>
            {vistaFechaLibre === 'general' && <ListaRanking datos={rankingLibre} loading={loadingLibre} empty="Sin datos libres" />}
            {vistaFechaLibre === 'fecha_actual' && <ListaRanking datos={rankingFechaLibre} loading={loadingFechaLibre} empty="Sin puntos para esta fecha" />}
          </div>
        </div>

        {/* ── COLUMNA 3: CLANES ── */}
        <div className={`ranking-col${tabMobile !== 'clanes' ? ' ranking-col-hidden' : ''}`} style={{ background: '#080d14', border: '1px solid #f59e0b33', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #0f172a', background: '#f59e0b08' }}>
            <h3 style={{ margin: 0, color: '#f59e0b', fontSize: '0.95rem', fontWeight: 'bold', letterSpacing: '1px' }}>CLUBES</h3>
          </div>
          <div style={{ padding: '12px' }}>
            {loadingClanes ? <p style={{ color: '#475569', textAlign: 'center', padding: '20px 0', fontSize: '0.85rem' }}>Cargando...</p>
            : rankingClanes.length === 0 ? <p style={{ color: '#334155', textAlign: 'center', padding: '20px 0', fontSize: '0.85rem' }}>Sin clubes</p>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {rankingClanes.map((c, i) => {
                  const medal = i < 3 ? MEDALLAS[i] : null;
                  return (
                    <div key={c.id} onClick={() => navigate('/clan')}
                      style={{ display: 'grid', gridTemplateColumns: '36px 1fr auto', padding: '9px 12px', background: '#0a0f19', border: `1px solid ${c.color}33`, borderRadius: '8px', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: '0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = c.color + '88'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = c.color + '33'}
                    >
                      <div style={{ textAlign: 'center' }}>
                        {medal ? <span style={{ fontSize: '1.1rem' }}>{medal}</span> : <span style={{ color: '#475569', fontWeight: 'bold', fontSize: '0.82rem' }}>#{i+1}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: c.color+'22', border: `2px solid ${c.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>
                          {EMBLEMAS[c.emblema] || ''}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, color: '#e2e8f0', fontWeight: 'bold', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.nombre} <span style={{ color: c.color, fontSize: '0.7rem' }}>[{c.tag}]</span>
                          </p>
                          <p style={{ margin: 0, color: '#475569', fontSize: '0.72rem' }}>👥 {c.totalMiembros} miembros</p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, color: c.puntosClan > 0 ? '#00f2ff' : '#334155', fontWeight: 'bold', fontSize: '0.95rem' }}>{c.puntosClan > 0 ? c.puntosClan : '—'}</p>
                        {c.medallasClan > 0 && <p style={{ margin: 0, color: '#f59e0b', fontSize: '0.7rem' }}>🏅 {c.medallasClan}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── COLUMNA 4: PENALES ── */}
        <div className={`ranking-col${tabMobile !== 'penales' ? ' ranking-col-hidden' : ''}`} style={{ background: '#080d14', border: '1px solid #22c55e33', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #0f172a', background: '#22c55e08' }}>
            <h3 style={{ margin: 0, color: '#22c55e', fontSize: '0.95rem', fontWeight: 'bold', letterSpacing: '1px' }}>PENALES</h3>
            <p style={{ margin: '2px 0 0', color: '#475569', fontSize: '0.72rem' }}>Mejor racha</p>
          </div>
          <div style={{ padding: '12px' }}>
            {loadingPenales ? (
              <p style={{ color: '#475569', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>Cargando...</p>
            ) : rankingPenales.length === 0 ? (
              <p style={{ color: '#334155', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>Sin datos aún</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {rankingPenales.map((r, i) => {
                  const esMio = r.id === session?.user?.id;
                  const medal = i < 3 ? MEDALLAS[i] : null;
                  return (
                    <div key={r.id}
                      onClick={() => navigate(`/palmares/${r.id}`)}
                      style={{ display: 'grid', gridTemplateColumns: '36px 1fr auto', padding: '9px 12px', background: esMio ? '#22c55e10' : '#080d14', border: `1px solid ${esMio ? '#22c55e44' : '#1e293b'}`, borderRadius: '8px', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: '0.2s' }}
                      onMouseEnter={e => { if (!esMio) e.currentTarget.style.borderColor = '#22c55e44'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = esMio ? '#22c55e44' : '#1e293b'; }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        {medal ? <span style={{ fontSize: '1.1rem' }}>{medal}</span> : <span style={{ color: '#475569', fontWeight: 'bold', fontSize: '0.82rem' }}>#{i+1}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: r.avatar_color || '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>
                          {r.nickname?.slice(0,2).toUpperCase()}
                        </div>
                        <span style={{ color: esMio ? '#22c55e' : '#e2e8f0', fontWeight: esMio ? 'bold' : '500', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.clan_tag && <span style={{ color: '#64748b' }}>[{r.clan_tag}] </span>}{r.nickname}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ margin: 0, color: '#22c55e', fontWeight: 'bold', fontSize: '1rem', fontFamily: 'monospace' }}>{r.racha_penales}</p>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.65rem' }}>goles</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default RankingGeneral;